import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { PlayIcon, SparklesIcon } from '@heroicons/react/24/solid';

gsap.registerPlugin(ScrollTrigger);

const PREMIUM_STORAGE_KEY = 'commitwatch_video_premium';

const DemoVideo = () => {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPremium] = useState(() => {
    const saved = localStorage.getItem(PREMIUM_STORAGE_KEY);
    return saved ? JSON.parse(saved) : false;
  });

  // Save premium status to localStorage
  useEffect(() => {
    localStorage.setItem(PREMIUM_STORAGE_KEY, JSON.stringify(isPremium));
  }, [isPremium]);

  const handlePlayVideo = () => {
    setIsPlaying(true);
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Header animation
      gsap.fromTo(
        headerRef.current?.children || [],
        { y: 60, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          stagger: 0.15,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: headerRef.current,
            start: 'top 80%',
            end: 'top 50%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Video container animation
      gsap.fromTo(
        videoRef.current,
        { y: 80, opacity: 0, scale: 0.95 },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          duration: 1.2,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: videoRef.current,
            start: 'top 85%',
            end: 'top 50%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="demo"
      className="relative py-24 sm:py-32 overflow-hidden"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-dark-950 via-dark-900/50 to-dark-950" />
      
      {/* Floating orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/6 w-[300px] h-[300px] bg-accent-500/10 rounded-full blur-[120px] animate-float" />
        <div className="absolute bottom-1/4 right-1/6 w-[250px] h-[250px] bg-purple-500/10 rounded-full blur-[100px] animate-float" style={{ animationDelay: '-3s' }} />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div ref={headerRef} className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass border border-white/10 mb-6">
            <PlayIcon className="w-4 h-4 text-accent-400" />
            <span className="text-sm font-medium text-dark-200">See It In Action</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
            <span className="text-white">Watch the </span>
            <span className="gradient-text">Demo</span>
          </h2>
          
          <p className="text-dark-300 text-base sm:text-lg max-w-2xl mx-auto">
            See how CommitWatch transforms your GitHub workflow with smart notifications
            and seamless integration.
          </p>
        </div>

        {/* Video Container */}
        <div
          ref={videoRef}
          className="relative group"
        >
          {/* Premium Badge */}
          {isPremium && (
            <div className="absolute -top-4 -right-4 z-20 flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full shadow-lg animate-pulse">
              <SparklesIcon className="w-4 h-4 text-dark-950" />
              <span className="text-sm font-bold text-dark-950">PREMIUM</span>
            </div>
          )}

          {/* Video Frame */}
          <div className={`relative rounded-3xl overflow-hidden border-2 transition-all duration-500 ${
            isPremium 
              ? 'border-amber-500/50 shadow-[0_0_60px_rgba(245,158,11,0.3)]' 
              : 'border-white/10 hover:border-accent-500/30'
          }`}>
            {/* Gradient border glow */}
            <div className={`absolute inset-0 rounded-3xl transition-opacity duration-500 ${
              isPremium 
                ? 'bg-gradient-to-r from-amber-500/20 via-yellow-500/20 to-amber-500/20 opacity-100' 
                : 'bg-gradient-to-r from-accent-500/0 via-purple-500/0 to-accent-500/0 opacity-0 group-hover:opacity-100'
            }`} />

            {/* Video/Thumbnail */}
            <div className="relative aspect-video bg-dark-900">
              {isPlaying ? (
                <video
                  className="absolute inset-0 w-full h-full object-cover"
                  src="/demo.mp4"
                  autoPlay
                  controls
                  playsInline
                />
              ) : (
                <>
                  {/* Thumbnail placeholder */}
                  <div className="absolute inset-0 bg-gradient-to-br from-dark-800 to-dark-900 flex items-center justify-center">
                    {/* Mock UI elements */}
                    <div className="w-full max-w-4xl px-8">
                      {/* Browser mockup */}
                      <div className="rounded-xl overflow-hidden border border-white/10 bg-dark-950/80 backdrop-blur">
                        {/* Browser header */}
                        <div className="flex items-center gap-2 px-4 py-3 bg-dark-800/50 border-b border-white/5">
                          <div className="flex gap-1.5">
                            <div className="w-3 h-3 rounded-full bg-red-500/80" />
                            <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                            <div className="w-3 h-3 rounded-full bg-green-500/80" />
                          </div>
                          <div className="flex-1 mx-4">
                            <div className="h-6 bg-dark-700/50 rounded-lg flex items-center px-3">
                              <span className="text-xs text-dark-400">github.com</span>
                            </div>
                          </div>
                        </div>
                        {/* Browser content */}
                        <div className="p-6 space-y-4">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-accent-500 to-emerald-500 flex items-center justify-center">
                              <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 2.824a9.176 9.176 0 110 18.352 9.176 9.176 0 010-18.352zM7.412 12a4.588 4.588 0 119.176 0 4.588 4.588 0 01-9.176 0z"/>
                              </svg>
                            </div>
                            <div>
                              <div className="h-4 w-32 bg-white/20 rounded" />
                              <div className="h-3 w-24 bg-white/10 rounded mt-2" />
                            </div>
                            <div className="ml-auto relative">
                              <div className="w-8 h-8 rounded-lg bg-accent-500/20 flex items-center justify-center">
                                <svg className="w-4 h-4 text-accent-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                </svg>
                              </div>
                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                                <span className="text-[10px] font-bold text-white">3</span>
                              </div>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="h-3 w-full bg-white/5 rounded" />
                            <div className="h-3 w-4/5 bg-white/5 rounded" />
                            <div className="h-3 w-3/5 bg-white/5 rounded" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Play button overlay */}
                  <button
                    onClick={handlePlayVideo}
                    className="absolute inset-0 flex items-center justify-center bg-dark-950/40 backdrop-blur-sm transition-all duration-300 group-hover:bg-dark-950/30"
                  >
                    <div className="relative">
                      {/* Pulse effect */}
                      <div className="absolute inset-0 w-24 h-24 sm:w-28 sm:h-28 bg-accent-500/30 rounded-full animate-ping" />
                      <div className="relative w-24 h-24 sm:w-28 sm:h-28 bg-gradient-to-br from-accent-500 to-emerald-500 rounded-full flex items-center justify-center shadow-glow-xl transition-transform duration-300 hover:scale-110">
                        <PlayIcon className="w-10 h-10 sm:w-12 sm:h-12 text-white ml-1" />
                      </div>
                    </div>
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Video caption */}
          <div className="mt-6 text-center">
            <p className="text-dark-400 text-sm">
              {isPremium ? (
                <span className="flex items-center justify-center gap-2">
                  <SparklesIcon className="w-4 h-4 text-amber-400" />
                  <span className="text-amber-400 font-medium">Premium content unlocked</span>
                </span>
              ) : (
                'Click to play the demo video'
              )}
            </p>
          </div>
        </div>

        {/* Feature highlights below video */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[
            { title: 'Easy Setup', desc: 'Install in under 30 seconds' },
            { title: 'Zero Config', desc: 'Works out of the box' },
            { title: 'Always Free', desc: 'No premium tiers' },
          ].map((item, index) => (
            <div
              key={index}
              className="text-center p-6 rounded-2xl glass border border-white/5 hover:border-accent-500/30 transition-all duration-300"
            >
              <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
              <p className="text-dark-400 text-sm">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default DemoVideo;
